/** The token-bearing shape a successful silent renewal resolves with. */
export type RefreshAuth = {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_at: number;
};

/**
 * Narrows the value {@link AuthService.signinSilent} resolves with.
 *
 * That method is declared `Promise<User>` but the declaration is not the whole truth: the
 * refresh-token grant resolves with `null` when no stored user is found, and the method
 * swallows its own failure and resolves with the error object. Reading a token field off
 * `null` throws; off the error object it merely yields `undefined`, which callers happen
 * to treat as "no renewal". Narrowing first makes both route to a clean logout.
 */
export const isRefreshAuth = (value: unknown): value is RefreshAuth => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id_token === 'string' &&
    typeof candidate.access_token === 'string' &&
    typeof candidate.expires_at === 'number' &&
    // Optional, but callers persist it verbatim — so a present-but-not-a-string value
    // has to fail here rather than reach storage.
    (candidate.refresh_token === undefined || typeof candidate.refresh_token === 'string')
  );
};
